import { useState } from 'react';
import { useParams } from 'react-router-dom';
import {Clock, CheckCircle } from 'lucide-react';
import { entranceExamCategories } from '../data/enteranceExamData';

// Add this interface for test series cards
interface TestSeriesCard {
  id: string;
  title: string;
  description: string;
  price: number;
  originalPrice: number;
  features: string[];
}

// Add interface for PYQ cards
interface PYQCard {
  id: string;
  year: number;
  month: string;
  totalQuestions: number;
  duration: string;
  price: number;
  originalPrice: number;
  features: string[];
}

const TestDetailPage = () => {
  const { examId } = useParams();
  const [activeTab, setActiveTab] = useState('overview');

  // Find the exam in the flat array
  const exam = entranceExamCategories[0].exams.find(e => e.id === examId);

  // Mock exam details (replace with real data if available)
  const examDetails = {
    description: `Comprehensive test series for ${exam?.title}`,
    duration: '2 hours',
    totalQuestions: 120,
    totalMarks: 480,
    sections: [
      { name: 'General Agriculture', questions: 40, marks: 160 },
      { name: exam?.title?.split(' ').slice(-1)[0] || 'Subject', questions: 80, marks: 320 }
    ]
  };

  // Mock data for test series cards
  const testSeriesCards: TestSeriesCard[] = [
    {
      id: '1',
      title: 'Complete Test Series',
      description: 'Full length mock tests with detailed solutions',
      price: 499,
      originalPrice: 999,
      features: [
        '10 Full Length Tests',
        'Detailed Solutions',
        'Performance Analysis',
        'Valid for 1 Year'
      ]
    },
    {
      id: '2',
      title: 'Topic Wise Tests',
      description: 'Practice tests for individual topics',
      price: 299,
      originalPrice: 599,
      features: [
        '20 Topic Wise Tests',
        'Chapter-wise Analysis',
        'Detailed Solutions',
        'Valid for 1 Year'
      ]
    }
  ];

  // Mock data for PYQ cards
  const pyqCards: PYQCard[] = [
    {
      id: '2023',
      year: 2023,
      month: 'September',
      totalQuestions: 120,
      duration: '2 hours',
      price: 149,
      originalPrice: 299,
      features: [
        'Original Question Paper',
        'Detailed Solutions',
        'Answer Key',
        'Important Notes'
      ]
    },
    {
      id: '2022',
      year: 2022,
      month: 'September',
      totalQuestions: 120,
      duration: '2 hours',
      price: 149,
      originalPrice: 299,
      features: [
        'Original Question Paper',
        'Detailed Solutions',
        'Answer Key',
        'Important Notes'
      ]
    },
    {
      id: '2021',
      year: 2021,
      month: 'September',
      totalQuestions: 120,
      duration: '2 hours',
      price: 149,
      originalPrice: 299,
      features: [
        'Original Question Paper',
        'Detailed Solutions',
        'Answer Key',
        'Important Notes'
      ]
    }
  ];

  if (!exam) {
    return <div>Exam not found</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Secondary Navigation */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex -mb-px">
            <button
              onClick={() => setActiveTab('overview')}
              className={`py-4 px-6 inline-flex items-center border-b-2 font-medium text-sm ${
                activeTab === 'overview'
                  ? 'border-green-500 text-green-600 dark:text-green-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
              aria-label="Overview"
            >
              Overview
            </button>
            <button
              onClick={() => setActiveTab('test-series')}
              className={`py-4 px-6 inline-flex items-center border-b-2 font-medium text-sm ${
                activeTab === 'test-series'
                  ? 'border-green-500 text-green-600 dark:text-green-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
              aria-label="Test Series"
            >
              Test Series
            </button>
            <button
              onClick={() => setActiveTab('pyqs')}
              className={`py-4 px-6 inline-flex items-center border-b-2 font-medium text-sm ${
                activeTab === 'pyqs'
                  ? 'border-green-500 text-green-600 dark:text-green-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
              aria-label="Previous Year Papers"
            >
              Previous Year Papers
            </button>
          </nav>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left Content - Blog Style */}
            <div className="lg:col-span-2 space-y-6">
              {/* Title Section */}
              <div className="bg-white dark:bg-gray-800 rounded-[24px] shadow-sm p-6">
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                  {exam.title}
                </h1>
                <div className="prose dark:prose-invert max-w-none">
                  <p className="text-gray-600 dark:text-gray-400 mb-4">
                    {examDetails.description}
                  </p>
                  <h2 className="text-xl font-semibold mt-8 mb-4">About the Exam</h2>
                  <p className="text-gray-600 dark:text-gray-400 mb-4">
                    This comprehensive test series is designed to help you prepare for {exam.title}. 
                    The exam is conducted for {examDetails.duration} and consists of {examDetails.totalQuestions} questions 
                    with a total of {examDetails.totalMarks} marks.
                  </p>
                  <h2 className="text-xl font-semibold mt-8 mb-4">Exam Pattern</h2>
                  <div className="overflow-x-auto mb-6">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                      <thead>
                        <tr>
                          <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-400">Section</th>
                          <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-400">Questions</th>
                          <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-400">Marks</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                        {examDetails.sections.map((section, index) => (
                          <tr key={index}>
                            <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{section.name}</td>
                            <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{section.questions}</td>
                            <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{section.marks}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <h2 className="text-xl font-semibold mt-8 mb-4">How to Prepare</h2>
                  <p className="text-gray-600 dark:text-gray-400 mb-4">
                    Follow these steps for effective preparation:
                  </p>
                  <ul className="list-disc pl-6 text-gray-600 dark:text-gray-400 space-y-2">
                    <li>Understand the complete syllabus</li>
                    <li>Practice with our topic-wise tests</li>
                    <li>Take full-length mock tests</li>
                    <li>Analyze your performance regularly</li>
                    <li>Focus on weak areas</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Right Sidebar - Test Series Cards */}
            <div className="space-y-6">
              {testSeriesCards.map((card) => (
                <div 
                  key={card.id}
                  className="bg-white dark:bg-gray-800 rounded-[24px] shadow-sm overflow-hidden"
                  aria-label="Test Series Card"
                >
                  <div className="p-6">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                      {card.title}
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                      {card.description}
                    </p>
                    <div className="flex items-baseline mb-4">
                      <span className="text-2xl font-bold text-gray-900 dark:text-white">₹{card.price}</span>
                      <span className="ml-2 text-sm text-gray-500 line-through">₹{card.originalPrice}</span>
                      <span className="ml-2 text-sm text-green-600 dark:text-green-400">
                        {Math.round(((card.originalPrice - card.price) / card.originalPrice) * 100)}% off
                      </span>
                    </div>
                    <ul className="space-y-2 mb-6">
                      {card.features.map((feature, index) => (
                        <li key={index} className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                          <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                    <button className="w-full py-2 px-4 bg-green-800 hover:bg-green-900 text-white rounded-[24px] text-sm font-medium transition-colors">
                      Buy Now
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'test-series' && (
          <div className="space-y-6">
            <div className="bg-white dark:bg-gray-800 rounded-[24px] shadow-sm p-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">
                Available Test Series
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {testSeriesCards.map((card) => (
                  <div 
                    key={card.id}
                    className="bg-gray-50 dark:bg-gray-900 rounded-[24px] shadow overflow-hidden"
                    aria-label="Test Series Card"
                  >
                    <div className="p-6">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                        {card.title}
                      </h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                        {card.description}
                      </p>
                      <div className="flex items-baseline mb-4">
                        <span className="text-2xl font-bold text-gray-900 dark:text-white">₹{card.price}</span>
                        <span className="ml-2 text-sm text-gray-500 line-through">₹{card.originalPrice}</span>
                        <span className="ml-2 text-sm text-green-600 dark:text-green-400">
                          {Math.round(((card.originalPrice - card.price) / card.originalPrice) * 100)}% off
                        </span>
                      </div>
                      <ul className="space-y-2 mb-6">
                        {card.features.map((feature, index) => (
                          <li key={index} className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                            <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                            {feature}
                          </li>
                        ))}
                      </ul>
                      <button className="w-full py-2 px-4 bg-green-800 hover:bg-green-900 text-white rounded-[24px] text-sm font-medium transition-colors">
                        Buy Now
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Additional Test Series Information */}
            <div className="bg-white dark:bg-gray-800 rounded-[24px] shadow-sm p-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                Why Choose Our Test Series?
              </h2>
              <div className="grid md:grid-cols-3 gap-6">
                <div className="space-y-2">
                  <h3 className="font-medium text-gray-900 dark:text-white">Quality Questions</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Carefully curated questions matching the actual exam pattern
                  </p>
                </div>
                <div className="space-y-2">
                  <h3 className="font-medium text-gray-900 dark:text-white">Detailed Solutions</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Step-by-step explanations for better understanding
                  </p>
                </div>
                <div className="space-y-2">
                  <h3 className="font-medium text-gray-900 dark:text-white">Performance Analysis</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Detailed analytics to track your progress
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'pyqs' && (
          <div className="space-y-6">
            <div className="bg-white dark:bg-gray-800 rounded-[24px] shadow-sm p-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">
                Previous Year Papers
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {pyqCards.map((card) => (
                  <div 
                    key={card.id}
                    className="bg-gray-50 dark:bg-gray-900 rounded-[24px] shadow overflow-hidden"
                    aria-label="PYQ Card"
                  >
                    <div className="p-6">
                      <div className="flex justify-between items-start mb-4">
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                            {card.year}
                          </h3>
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            {card.month} Edition
                          </p>
                        </div>
                        <span className="px-3 py-1 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded-full text-xs font-medium">
                          {card.totalQuestions} Questions
                        </span>
                      </div>

                      <div className="flex items-center space-x-3 text-sm text-gray-600 dark:text-gray-400 mb-4">
                        <div className="flex items-center">
                          <Clock className="h-4 w-4 mr-1" />
                          <span>{card.duration}</span>
                        </div>
                      </div>

                      <div className="flex items-baseline mb-4">
                        <span className="text-2xl font-bold text-gray-900 dark:text-white">₹{card.price}</span>
                        <span className="ml-2 text-sm text-gray-500 line-through">₹{card.originalPrice}</span>
                        <span className="ml-2 text-sm text-green-600 dark:text-green-400">
                          {Math.round(((card.originalPrice - card.price) / card.originalPrice) * 100)}% off
                        </span>
                      </div>

                      <ul className="space-y-2 mb-6">
                        {card.features.map((feature, index) => (
                          <li key={index} className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                            <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                            {feature}
                          </li>
                        ))}
                      </ul>

                      <div className="space-y-2">
                        <button className="w-full py-2 px-4 bg-green-800 hover:bg-green-900 text-white rounded-[24px] text-sm font-medium transition-colors" aria-label="Buy Now">
                          Buy Now
                        </button>
                        <button className="w-full py-2 px-4 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-[24px] text-sm font-medium transition-colors" aria-label="Preview">
                          Preview
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Additional Information */}
            <div className="bg-white dark:bg-gray-800 rounded-[24px] shadow-sm p-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                Why Practice with Previous Year Papers?
              </h2>
              <div className="grid md:grid-cols-3 gap-6">
                <div className="space-y-2">
                  <h3 className="font-medium text-gray-900 dark:text-white">Understand the Pattern</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Get familiar with actual exam question patterns and difficulty level
                  </p>
                </div>
                <div className="space-y-2">
                  <h3 className="font-medium text-gray-900 dark:text-white">Important Topics</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Identify frequently asked topics and important concepts
                  </p>
                </div>
                <div className="space-y-2">
                  <h3 className="font-medium text-gray-900 dark:text-white">Time Management</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Practice with actual exam time constraints
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TestDetailPage; 